import {
  FC, useEffect, useRef, useState,
} from 'react'

import { GeneratedSite, formatEuros, sitePriceVariant } from '@api/generated-site'
import { useSiteOffers, useCancelSiteSubscription, useMarkSiteAdInterest } from '@hooks/queries/generated-site'
import SiteCountdown from './SiteCountdown'
import OfferPanel from './OfferPanel'
import SiteBenefitsPanel, { trialCtaLabel } from './SiteBenefitsPanel'
import { SiteAdsPreview } from './SiteBenefits'
import EditPanel from './EditPanel'
import DomainPanel from './DomainPanel'

type Props = {
  site: GeneratedSite
  // séquence d'introduction plein écran (tunnel) ou affichage direct (espace client)
  withIntro?: boolean
  onContinue?: () => void
  continueLabel?: string
  onEvent?: (name: string) => void
  // nom de domaine inclus : proposé dans l'espace client seulement, pas dans le tunnel
  withDomain?: boolean
  // ouvre l'offre dès l'affichage (lien ?offer=1 des emails et de la page publique)
  autoOpenOffer?: boolean
  // enregistre l'intérêt pour la publicité dès l'affichage (lien &pub=1 du bouton « Je veux lancer cette publicité »)
  autoAdInterest?: boolean
}

const DOMAIN_IN_PROGRESS = ['RESERVING', 'REGISTERED', 'DNS_CONFIGURED', 'CERT_PENDING']

const domainButtonLabel = (site: GeneratedSite) => {
  if (!site.domain || site.domainStatus === 'FAILED') return 'Choisir mon nom de domaine (inclus)'
  if (DOMAIN_IN_PROGRESS.includes(site.domainStatus || '')) return `${site.domain} : activation en cours`
  return site.domainVerificationPending ? `${site.domain} : une confirmation vous est demandée` : `${site.domain} : domaine actif`
}

// l'URL d'aperçu porte la version courante du HTML : un changement de version recharge l'iframe et contourne
// les caches (navigateur, service des sites : hebergement.md §6.2). Jamais sur l'URL publique du site.
const versionedPreviewUrl = (site: GeneratedSite) => {
  if (!site.previewUrl) return null
  return site.htmlVersion ? `${site.previewUrl}?v=${site.htmlVersion}` : site.previewUrl
}

const FAILED_EDIT_BANNER_MS = 10 * 60 * 1000

// dernière modification (la plus récente, tri serveur) en échec depuis moins de 10 min : l'échec d'une
// modification asynchrone arrive par le poll, et l'information survit à un rechargement de la page
const recentFailedEdit = (site: GeneratedSite) => {
  const last = site.edits[0]
  if (!last || last.status !== 'FAILED') return null
  return Date.now() - new Date(last.createdAt).getTime() < FAILED_EDIT_BANNER_MS ? last : null
}

const INTRO_LINES = [
  'Vous nous avez décrit votre activité.',
  'Pendant que vous signiez votre contrat…',
  '…nous avons construit ceci.',
]
const INTRO_STEP_MS = 1500
const INTRO_HOLD_MS = 900

const prefersReducedMotion = () => typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const VALUE_POINTS = [
  { title: 'Des prospects, pas juste une vitrine', text: 'Un formulaire de contact et un bouton d’appel : vos clients vous trouvent et vous écrivent dès cette semaine.' },
  { title: 'La crédibilité d’une vraie entreprise', text: 'Avant de vous appeler, vos clients vous cherchent sur Google. Ils tombent sur ce site, pas sur rien.' },
  { title: 'Et bientôt, votre publicité', text: 'Vous voulez plus de clients ? Nous préparons des publicités prêtes à diffuser pour votre activité, à partir de ce site.' },
]

const formatDate = (value: string) => new Date(value).toLocaleDateString('fr-FR')

// site retiré de la ligne : réactivable par le client lui-même sauf retrait manuel par l'équipe
const REACTIVATABLE_REASONS = ['trial_canceled', 'subscription_ended']
const unpublishedLine = (site: GeneratedSite) => {
  const date = site.unpublishedAt ? formatDate(site.unpublishedAt) : ''
  if (site.unpublishReason === 'trial_canceled') return `Vous avez arrêté votre essai le ${date} : rien ne vous a été facturé.`
  if (site.unpublishReason === 'subscription_ended') return `Votre abonnement a pris fin et votre site a été retiré de la ligne le ${date}.`
  return `Ce site a été retiré de la ligne par notre équipe le ${date}.`
}

// révélation du site « surprise » : intro hypnotique noir et blanc, puis aperçu qui s'agrandit, compte à rebours et appels à l'action
const SiteReveal: FC<Props> = ({
  site, withIntro = true, onContinue, continueLabel = 'Terminer mon inscription', onEvent, withDomain = false, autoOpenOffer = false, autoAdInterest = false,
}) => {
  const [introStep, setIntroStep] = useState(withIntro && !prefersReducedMotion() ? 0 : INTRO_LINES.length + 1)
  // 'benefits' = micro landing page des avantages, montrée avant les formules ('offer')
  const [panel, setPanel] = useState<'benefits' | 'offer' | 'edit' | 'domain' | null>(autoOpenOffer && !site.purchasedAt ? 'benefits' : null)
  const [justSubscribed, setJustSubscribed] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const cancelSubscription = useCancelSiteSubscription()
  const markAdInterest = useMarkSiteAdInterest()
  const introDone = introStep > INTRO_LINES.length
  const brand = site.commercialBrand || 'votre entreprise'
  const previewUrl = versionedPreviewUrl(site)
  const failedEdit = recentFailedEdit(site)

  // A/B test de prix : grille tirée par le serveur à la création du site ; la grille affichée ici et dans
  // OfferPanel partage la même entrée de cache
  const priceVariant = sitePriceVariant(site)
  const offers = useSiteOffers(priceVariant)
  // accroche « X € HT/mois sans engagement » : prix du plan mensuel, présélectionné dans OfferPanel
  const monthlyPlan = offers.data?.plans.find((plan) => plan.key === 'month')
    || offers.data?.plans[0]
  const monthlyFromHt = monthlyPlan ? monthlyPlan.monthlyEquivalentHt : null
  // essai gratuit du mensuel (0 = pas d'essai) : annoncé par le bouton principal, présélectionné
  // dans le panneau des formules ; jamais pour une réactivation (site déjà activé une fois)
  const trialDays = site.purchasedAt ? 0 : (monthlyPlan?.trialDays ?? 0)
  const canReactivate = !!site.unpublishedAt && REACTIVATABLE_REASONS.includes(site.unpublishReason || '')
  const card = site.paymentLast4 ? ` •••• ${site.paymentLast4}` : ''
  const priceLine = monthlyFromHt !== null ? `${formatEuros(monthlyFromHt)} HT/mois sans engagement · ` : ''
  const ctaSubline = trialDays > 0
    ? `${monthlyFromHt !== null ? `0 € aujourd’hui, puis ${priceLine}` : ''}carte${card} déjà enregistrée, rien à ressaisir`
    : `${priceLine}payé avec votre carte${card} en un clic`

  useEffect(() => {
    if (introDone) return undefined
    const delay = introStep === INTRO_LINES.length ? INTRO_HOLD_MS : INTRO_STEP_MS
    const timer = window.setTimeout(() => setIntroStep((step) => step + 1), delay)
    return () => window.clearTimeout(timer)
  }, [introStep, introDone])

  const track = (name: string) => onEvent && onEvent(name)

  const openPlans = () => { track('site-offer-open'); setPanel('offer') }
  // le bouton principal ouvre d'abord les avantages (micro landing page), puis les formules ;
  // un client qui réactive son site connaît déjà l'offre, il va droit aux formules
  const openOffer = () => {
    if (site.purchasedAt) { openPlans(); return }
    track('site-benefits-open')
    setPanel('benefits')
  }
  const openEdit = () => { track('site-edit-open'); setPanel('edit') }
  const openDomain = () => { track('site-domain-open'); setPanel('domain') }
  const onAdInterest = () => { track('site-ad-interest'); markAdInterest.mutate(undefined) }
  // arrivée depuis la page publique avec &pub=1 : la demande est enregistrée une seule fois
  const autoAdInterestDone = useRef(false)
  useEffect(() => {
    if (!autoAdInterest || site.adInterestAt || autoAdInterestDone.current) return
    autoAdInterestDone.current = true
    onAdInterest()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAdInterest, site.adInterestAt])
  const onCancel = () => { track('site-cancel'); setConfirmCancel(false); cancelSubscription.mutate(undefined) }

  if (!introDone) {
    return (
      <button
        type="button"
        onClick={() => setIntroStep(INTRO_LINES.length + 1)}
        className="fixed inset-0 z-40 flex items-center justify-center bg-black px-6 text-center cursor-default"
        aria-label="Passer l’introduction"
      >
        <div className="max-w-[720px]">
          {INTRO_LINES.slice(0, Math.min(introStep + 1, INTRO_LINES.length)).map((line, index) => (
            <p
              key={line}
              className={`animate-fade-up font-aeonik leading-tight text-white ${index === INTRO_LINES.length - 1 ? 'text-3xl md:text-5xl font-medium mt-6' : 'text-xl md:text-3xl text-white/70'} ${index > 0 ? 'mt-3' : ''}`}
            >
              {line}
            </p>
          ))}
        </div>
        <div className="absolute bottom-8 text-[11px] uppercase tracking-[0.25em] text-white/30">Cliquez pour passer</div>
      </button>
    )
  }

  return (
    <div className="w-full">
      <div className="rounded-[28px] bg-black text-white px-5 py-8 md:px-10 md:py-12 overflow-hidden">
        <div className="text-center max-w-[760px] mx-auto animate-fade-up">
          <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-3">Votre surprise</div>
          <h1 className="[&&&]:text-3xl md:[&&&]:text-5xl font-aeonik font-medium leading-[1.05] text-white [&&&]:mb-4">
            {site.purchasedAt ? `Le site de ${brand} est en ligne.` : `Le site de ${brand} existe déjà.`}
          </h1>
          <p className="text-white/70 text-base md:text-lg">
            {site.tagline ? `« ${site.tagline} »` : 'Créé à partir de ce que vous nous avez dit, en moins de deux minutes.'}
          </p>
        </div>

        <div className="relative mt-8 md:mt-10 mx-auto max-w-[1040px] animate-reveal-scale" style={{ animationDelay: '250ms' }}>
          <div className="rounded-2xl overflow-hidden bg-white animate-soft-glow">
            <div className="flex items-center gap-1.5 px-3 py-2 bg-[#f3f3f3] border-b border-black/5">
              <span className="h-2.5 w-2.5 rounded-full bg-black/20" />
              <span className="h-2.5 w-2.5 rounded-full bg-black/20" />
              <span className="h-2.5 w-2.5 rounded-full bg-black/20" />
              <span className="ml-3 text-[11px] text-black/50 truncate">{site.publishedUrl || `${brand} · aperçu`}</span>
            </div>
            <div className="relative">
              {previewUrl ? (
                <iframe
                  title={`Site de ${brand}`}
                  src={previewUrl}
                  className="w-full h-[440px] md:h-[640px] bg-white"
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                  loading="eager"
                />
              ) : site.thumbnailUrl ? (
                <img src={site.thumbnailUrl} alt={`Site de ${brand}`} className="w-full" />
              ) : (
                <div className="h-[320px] flex items-center justify-center text-black/50 text-sm">Aperçu indisponible pour le moment</div>
              )}
              {site.isUpdating && (
                <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] flex flex-col items-center justify-center text-white px-6 text-center">
                  <div className="h-10 w-10 rounded-full border-2 border-white/30 border-t-white animate-spin mb-4" />
                  <div className="font-aeonik text-xl font-medium">Votre site se met à jour</div>
                  <div className="text-white/70 text-sm mt-1">Une à deux minutes. Vous pouvez continuer, nous vous prévenons quand c’est prêt.</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {failedEdit && (
          <div className="mx-auto mt-4 max-w-[1040px] rounded-2xl border border-red-400/40 bg-red-500/15 px-5 py-3 text-center text-sm text-red-100">
            {failedEdit.refused && 'Cette modification n’a pas pu être appliquée (contenu non pris en charge)'}
            {failedEdit.refused && (failedEdit.creditsCost > 0 ? ', vos crédits ont été recrédités. ' : '. ')}
            {failedEdit.refused && 'Besoin d’aide ? Écrivez-nous depuis la rubrique Aide.'}
            {!failedEdit.refused && (failedEdit.creditsCost > 0
              ? 'La mise à jour de votre site a échoué, vos crédits ont été recrédités. Vous pouvez réessayer.'
              : 'La mise à jour de votre site a échoué. Vous pouvez réessayer.')}
          </div>
        )}

        <div className="mt-8 md:mt-10 grid md:grid-cols-[1.1fr_0.9fr] gap-6 md:gap-10 max-w-[1040px] mx-auto">
          <div className="space-y-5 animate-fade-up" style={{ animationDelay: '900ms' }}>
            {VALUE_POINTS.map((point, index) => (
              <div key={point.title} className="flex gap-4">
                <div className="shrink-0 h-8 w-8 rounded-full border border-white/20 flex items-center justify-center font-aeonik text-sm text-white/80">{index + 1}</div>
                <div>
                  <div className="font-aeonik text-base md:text-lg font-medium">{point.title}</div>
                  <div className="text-sm text-white/60 mt-0.5">{point.text}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="animate-fade-up" style={{ animationDelay: '1200ms' }}>
            {!site.purchasedAt && <SiteCountdown expiresAt={site.expiresAt} />}

            {site.unpublishedAt && (
              <div className="rounded-2xl border border-white/15 bg-white/5 px-5 py-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mb-1">Site retiré de la ligne</div>
                <div className="text-sm text-white/70">{unpublishedLine(site)}</div>
                {withDomain && canReactivate && (
                  <button
                    type="button"
                    onClick={openOffer}
                    className="mt-4 w-full rounded-2xl bg-white text-black py-4 px-5 text-left hover:bg-white/90 transition-colors"
                  >
                    <div className="font-aeonik text-lg font-medium leading-tight">Remettre mon site en ligne</div>
                    <div className="text-xs text-black/60 mt-1">{`${priceLine}payé avec votre carte${card} en un clic`}</div>
                  </button>
                )}
                {!canReactivate && (
                  <div className="mt-2 text-xs text-white/50">Pour le remettre en ligne, écrivez-nous depuis la rubrique Aide.</div>
                )}
              </div>
            )}
            {!site.unpublishedAt && (site.purchasedAt ? (
              <div className="rounded-2xl border border-white/15 bg-white/5 px-5 py-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mb-1">{site.trialEndsAt ? 'Essai gratuit en cours' : 'Site activé'}</div>
                {site.publishedUrl ? (
                  <a href={site.publishedUrl} target="_blank" rel="noopener noreferrer" className="font-aeonik text-lg underline underline-offset-4 break-all">{site.publishedUrl}</a>
                ) : (
                  <div className="text-sm text-white/70">Mise en ligne en cours, l’adresse arrive par email.</div>
                )}
                {site.trialEndsAt && (
                  <div className="mt-2 text-sm text-white/70">
                    {`Gratuit jusqu’au ${formatDate(site.trialEndsAt)}${monthlyFromHt !== null ? `, puis ${formatEuros(monthlyFromHt)} HT/mois sans engagement` : ''}.`}
                  </div>
                )}
                <div className="mt-2 text-sm text-white/70">{`${site.creditsBalance} crédits de modification disponibles`}</div>
                {withDomain && site.domainOfferAvailable && (
                  <button type="button" onClick={openDomain} className="mt-3 block text-left text-sm underline underline-offset-4 text-white/80 hover:text-white">
                    {domainButtonLabel(site)}
                  </button>
                )}
                {withDomain && !site.unpublishedAt && (
                  <div className="mt-3 text-xs text-white/50">
                    {!confirmCancel && !cancelSubscription.isSuccess && (
                      <button type="button" onClick={() => setConfirmCancel(true)} className="underline underline-offset-4 hover:text-white/80">
                        {site.trialEndsAt ? 'Arrêter mon essai' : 'Résilier mon abonnement'}
                      </button>
                    )}
                    {confirmCancel && (
                      <span>
                        {site.trialEndsAt ? 'Votre site sera retiré de la ligne tout de suite et rien ne vous sera facturé. ' : 'Votre site restera en ligne jusqu’à la fin de la période déjà réglée. '}
                        <button type="button" onClick={onCancel} disabled={cancelSubscription.isPending} className="underline underline-offset-4 text-white/80 hover:text-white">Confirmer</button>
                        {' · '}
                        <button type="button" onClick={() => setConfirmCancel(false)} className="underline underline-offset-4 hover:text-white/80">Garder mon site</button>
                      </span>
                    )}
                    {cancelSubscription.isSuccess && (
                      <span>C’est fait : votre abonnement est arrêté.</span>
                    )}
                    {cancelSubscription.isError && (
                      <span className="text-red-200">L’arrêt n’a pas pu être enregistré, écrivez-nous depuis la rubrique Aide.</span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={openOffer}
                  className="mt-4 w-full rounded-2xl bg-white text-black py-4 px-5 text-left hover:bg-white/90 transition-colors"
                >
                  <div className="font-aeonik text-lg font-medium leading-tight">
                    {trialDays > 0 ? trialCtaLabel(trialDays) : 'Mettre mon site en ligne maintenant'}
                  </div>
                  <div className="text-xs text-black/60 mt-1">{ctaSubline}</div>
                </button>
                <div className="mt-2 text-center text-xs text-white/50">
                  Un site vitrine chez un prestataire : 1 500 à 3 000 € et trois semaines.
                  {' '}
                  Le vôtre est déjà là.
                </div>
              </>
            ))}

            <button
              type="button"
              onClick={openEdit}
              className="mt-3 w-full rounded-2xl border border-white/25 py-4 px-5 text-left hover:border-white/60 transition-colors"
            >
              <div className="font-aeonik text-lg font-medium leading-tight flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white text-black">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Z" />
                    <path d="M19 11a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.92V20H8a1 1 0 1 0 0 2h8a1 1 0 1 0 0-2h-3v-2.08A7 7 0 0 0 19 11Z" />
                  </svg>
                </span>
                Modifier mon site à la voix
              </div>
              <div className="text-xs text-white/60 mt-1">
                {site.freeEditsRemaining > 0
                  ? 'Première modification offerte : dites ce que vous voulez changer, c’est fait en deux minutes.'
                  : `${site.editCost} crédits par modification · solde ${site.creditsBalance}`}
              </div>
            </button>

            {previewUrl && (
              <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="mt-3 block text-center text-sm text-white/60 underline underline-offset-4 hover:text-white">
                Ouvrir le site en grand
              </a>
            )}
            {withDomain && site.publicUrl && !site.purchasedAt && (
              <a href={site.publicUrl} target="_blank" rel="noopener noreferrer" className="mt-2 block text-center text-sm text-white/60 underline underline-offset-4 hover:text-white">
                Partager mon site (lien sans connexion)
              </a>
            )}
            {withDomain && (
              <button
                type="button"
                onClick={onAdInterest}
                disabled={!!site.adInterestAt || markAdInterest.isPending}
                className="mt-3 block w-full text-center text-sm text-white/60 underline underline-offset-4 hover:text-white disabled:no-underline disabled:text-white/70"
              >
                {site.adInterestAt ? 'Publicité : c’est noté, nous revenons vers vous.' : 'Je veux une publicité pour mon activité'}
              </button>
            )}
          </div>
        </div>
      </div>

      {withDomain && site.ads && (
        <div className="mt-10">
          <SiteAdsPreview ads={site.ads} brand={brand} onLaunch={onAdInterest} launchRecorded={!!site.adInterestAt} />
        </div>
      )}

      {onContinue && (
        <button type="button" onClick={onContinue} className="mt-5 w-full text-sm text-new-dark-gray underline">
          {continueLabel}
        </button>
      )}

      {panel === 'benefits' && (
        <SiteBenefitsPanel
          site={site}
          monthlyAmountHt={monthlyFromHt}
          trialDays={trialDays}
          onClose={() => setPanel(null)}
          onContinue={openPlans}
          onAdInterest={withDomain ? onAdInterest : undefined}
        />
      )}
      {panel === 'offer' && (
        <OfferPanel
          site={site}
          priceVariant={priceVariant}
          onClose={() => setPanel(null)}
          onSubscribed={() => { track('site-subscribed'); setJustSubscribed(true); setPanel('edit') }}
        />
      )}
      {panel === 'edit' && (
        <EditPanel
          site={site}
          priceVariant={priceVariant}
          onClose={() => setPanel(null)}
          onOpenOffer={() => setPanel('offer')}
          onRequested={() => { track('site-edit-requested'); setPanel(null) }}
        />
      )}
      {panel === 'domain' && <DomainPanel site={site} onClose={() => setPanel(null)} />}
      {justSubscribed && panel === 'edit' && (
        <div className="fixed left-1/2 top-6 z-[60] -translate-x-1/2 rounded-full bg-white text-black px-5 py-2 text-sm font-medium shadow-2xl animate-fade-up">
          Votre site est activé. Et si on l’améliorait tout de suite ?
        </div>
      )}
    </div>
  )
}

export default SiteReveal
